import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const AnalyticsPanel = ({ analytics }) => {
  const [selectedMetric, setSelectedMetric] = useState('occupancy');

  const occupancyColors = {
    high: '#10b981', // success color
    medium: '#f59e0b', // warning color  
    low: '#ef4444' // destructive color
  };

  const getOccupancyLevel = (rate) => {
    if (rate >= 80) return 'high';
    if (rate >= 60) return 'medium';
    return 'low';
  };

  const peakHoursData = analytics?.peakHours?.map((item) => ({
    ...item,
    fill: '#6366f1' // primary color
  }));

  const occupancyData = analytics?.occupancyTrend?.map((item) => ({
    ...item,
    fill: occupancyColors?.[getOccupancyLevel(item?.occupancy)]
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg shadow-elevation-3 p-3">
          <p className="text-sm font-medium text-card-foreground mb-1">
            {label}
          </p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {selectedMetric === 'occupancy' ? `Taxa: ${entry?.value}%` : `Reservas: ${entry?.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    const data = selectedMetric === 'occupancy' ? occupancyData : peakHoursData;
    const dataKey = selectedMetric === 'occupancy' ? 'occupancy' : 'bookings';

    return (
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
          <XAxis 
            dataKey={selectedMetric === 'occupancy' ? 'date' : 'time'} 
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickFormatter={(value) => {
              if (selectedMetric === 'occupancy') {
                return new Date(value)?.toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: '2-digit' 
                });
              }
              return value;
            }}
          />
          <YAxis 
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickFormatter={(value) => selectedMetric === 'occupancy' ? `${value}%` : value}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey={dataKey}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-heading font-bold text-card-foreground mb-1">
            Analytics
          </h3>
          <p className="text-sm text-muted-foreground">
            Dados de ocupação e horários de pico
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant={selectedMetric === 'occupancy' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedMetric('occupancy')}
          >
            Ocupação
          </Button>
          <Button
            variant={selectedMetric === 'peak' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedMetric('peak')}
          >
            Picos
          </Button>
        </div>
      </div>

      {/* Chart */}
      <div className="mb-6">
        {renderChart()}
      </div>

      {/* Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {selectedMetric === 'occupancy' ? (
          <>
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-success/20 rounded-lg">
                  <Icon name="TrendingUp" size={16} className="text-success" />
                </div>
                <div>
                  <div className="text-sm font-medium text-success">
                    Maior ocupação
                  </div>
                  <div className="text-xs text-muted-foreground">
                    84% na terça-feira
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Icon name="Calendar" size={16} className="text-primary" />
                </div>
                <div>
                  <div className="text-sm font-medium text-primary">
                    Média semanal
                  </div>
                  <div className="text-xs text-muted-foreground">
                    72% de ocupação
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-warning/20 rounded-lg">
                  <Icon name="Clock" size={16} className="text-warning" />
                </div>
                <div>
                  <div className="text-sm font-medium text-warning">
                    Horário de pico
                  </div>
                  <div className="text-xs text-muted-foreground">
                    18:00-19:00 (42 reservas)
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-muted/30 border border-muted/40 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-muted/40 rounded-lg">
                  <Icon name="Moon" size={16} className="text-muted-foreground" />
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Menor movimento
                  </div>
                  <div className="text-xs text-muted-foreground">
                    06:00-08:00 (8 reservas)
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Additional Stats */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-lg font-heading font-bold text-card-foreground">
              174
            </div>
            <div className="text-xs text-muted-foreground">Reservas esta semana</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-heading font-bold text-card-foreground">
              4.6
            </div>
            <div className="text-xs text-muted-foreground">Nota média</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-heading font-bold text-card-foreground">
              89%
            </div>
            <div className="text-xs text-muted-foreground">Taxa de retenção</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;